<template>
  <v-dialog v-model="dialogSearch" persistent width="1400">
    <v-card tile>
      <v-card-title>
        <h1 class="container-title">{{ $t("navSearch") }}</h1>
      </v-card-title>
      <v-card-text>
        <v-layout class="wrap align-baseline mt-2 mb-8" style="gap: 10px">
          <v-text-field
            style="max-width: 300px"
            v-model="searchField"
            outlined
            :label="$t('searchLabel')"
            type="text"
            required
            :error-messages="phoneErrors"
            @input="$v.searchField.$touch()"
            @blur="$v.searchField.$touch()"
          ></v-text-field>
          <v-btn
            color="primary"
            :disabled="
              phoneErrors.length > 0 || searchField.length === 0 || !phoneValid
            "
            @click="sendSearchRequest(searchField)"
          >
            {{ $t("searchBtn") }}
          </v-btn>
        </v-layout>
        <v-data-table
          v-if="orders"
          :headers="headers"
          :items="orders"
          :items-per-page="-1"
          class="elevation-1"
          :no-data-text="$t('ordersNotFound')"
          hide-default-footer
        >
          <template v-slot:item.session_date="{ item }">
            <span>{{ item.session_date | dateFilter }}</span>
          </template>
          <template v-slot:item.session_time="{ item }">
            <span>{{ item.session_time | timeFilter }}</span>
          </template>
          <template v-slot:item.cost="{ item }">
            <span
              v-html="
                item.currency && item.currency.is_before
                  ? item.currency.code + item.cost
                  : item.cost + ' ' + item.currency.code
              "
            ></span>
          </template>
          <template v-slot:item.link="{ item }">
            <v-btn
              link
              text
              outlined
              color="primary"
              target="_blank"
              :to="{ name: 'order', params: { order_id: item.order_id } }"
              class="btn-search-order"
            >
              {{ $t("searchOrderGo") }}
            </v-btn>
          </template>
        </v-data-table>
        <v-skeleton-loader
          v-else
          class="mx-n3 mt-n4"
          type="table-thead, table-tbody, table-tfoot"
          tile
        ></v-skeleton-loader>
        <v-row class="text-center pt-6">
          <v-col sm="2" class="py-5">
            <v-select
              item-text="text"
              item-value="item"
              prepend-icon="mdi-cash"
              dense
              :label="$t('searchLimit')"
              :items="limits"
              v-model="selectedLimit"
              @change="uploadLimitPage"
            >
              <template slot="no-data">
                <span class="grey--text caption ml-1">{{
                  $t("placeholderLoading")
                }}</span>
              </template>
              <template v-slot:selection="{ item, index }">
                <span v-if="index === 0">{{ item }}</span>
              </template>
            </v-select>
          </v-col>
          <v-col v-if="totalPages > 1" sm="7" class="py-0">
            <div
              style="
                display: flex;
                justify-content: center;
                align-items: center;
              "
            >
              <button
                v-if="currentPage > 1"
                style="margin-right: 10px"
                @click="getPreviousPage"
              >
                <v-icon>mdi-skip-previous</v-icon>
              </button>
              <div v-for="page in totalPages" :key="page">
                <div
                  style="padding: 5px; margin-right: 10px; cursor: pointer"
                  :style="{ color: page === currentPage ? '#FF0000' : '' }"
                  @click="changePage(page)"
                  v-if="
                    (currentPage - page < 2 && currentPage - page > -2) ||
                    page === 1 ||
                    page === totalPages
                  "
                >
                  {{ page }}
                </div>
                <div
                  v-if="
                    (currentPage - page === 2 && currentPage !== 3) ||
                    (currentPage - page === -2 &&
                      totalPages - currentPage !== 2)
                  "
                  style="padding: 5px; margin-right: 10px"
                >
                  <p>...</p>
                </div>
              </div>
              <button v-if="currentPage < totalPages" @click="getNextPage">
                <v-icon>mdi-skip-next</v-icon>
              </button>
            </div>
          </v-col>
          <v-btn
            color="primary"
            @click="closeModal()"
            style="margin-left: auto; margin-right: 20px"
            >{{ $t("btnClose") }}</v-btn
          >
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { validationMixin } from "vuelidate";
import { required, helpers } from "vuelidate/lib/validators";
import moment from "moment/moment";

const phoneValid = (value) => {
  return value ? true : false;
};
const phoneRegexp = helpers.regex("phone", /^\+?[0-9]+$/);

export default {
  name: "SearchModal",

  mixins: [validationMixin],

  props: ["dialogSearch"],

  data() {
    return {
      orders: [],
      headers: [
        {
          text: this.$i18n.t("searchOrderNumber"),
          value: "order_id",
          width: "20%",
        },
        {
          text: this.$i18n.t("BAOrderDate"),
          value: "session_date",
          width: "20%",
        },
        {
          text: this.$i18n.t("BAOrderTime"),
          value: "session_time",
          width: "20%",
        },
        { text: this.$i18n.t("searchCost"), value: "cost", width: "20%" },
        { text: this.$i18n.t("searchOrderLink"), value: "link", width: "20%" },
      ],
      searchField: "",
      phoneValid: true,
      totalPages: null,
      offset: 0,
      limits: [10, 15, 30, 50, 100],
      selectedLimit: 10,
      currentPage: 1,
    };
  },
  computed: {
    ...mapGetters(["location"]),
    phoneErrors() {
      const errors = [];
      if (!this.$v.searchField.$dirty) return errors;
      !this.$v.searchField.required &&
        errors.push(this.$i18n.t("validationRequired"));
      !this.$v.searchField.phoneRegexp &&
        errors.push(this.$i18n.t("validationPhone"));
      !this.$v.phoneValid.phoneValid &&
        errors.push(this.$i18n.t("validationPhone"));
      return errors;
    },
  },
  validations: {
    searchField: {
      required,
      phoneRegexp,
    },
    phoneValid: {
      phoneValid,
    },
  },
  filters: {
    dateFilter(value) {
      return moment.parseZone(value).format("DD.MM.YYYY");
    },
    timeFilter(value) {
      return moment.parseZone(value, "HH:mm:ss").format("HH:mm");
    },
  },
  methods: {
    ...mapActions(["getSearchOrders"]),
    sendSearchRequest() {
      this.orders = null;
      this.offset = this.selectedLimit * (this.currentPage - 1);
      this.getSearchOrders({
        limit: this.selectedLimit,
        offset: this.offset,
        phone: this.searchField,
      })
        .then((response) => {
          if (response.data.results) {
            this.orders = response.data.results;
            this.totalPages = Math.ceil(
              response.data.count / this.selectedLimit
            );
          } else {
            this.orders = [];
          }
        })
        .catch((error) => {
          this.phoneValid = !error.response.data.phone;
          this.$v.phoneValid.$touch();
        });
      this.clearSearchField();
    },
    clearSearchField() {
      this.$v.$reset();
      this.phoneValid = true;
    },
    getPreviousPage() {
      this.currentPage -= 1;
      this.sendSearchRequest();
    },
    getNextPage() {
      this.currentPage += 1;
      this.sendSearchRequest();
    },
    changePage(page) {
      this.currentPage = page;
      this.sendSearchRequest();
    },
    uploadLimitPage() {
      this.currentPage = 1;
      this.sendSearchRequest();
    },
    closeModal() {
      this.searchField = "";
      this.orders = [];
      this.totalPages = null;
      this.currentPage = 1;
      this.selectedLimit = 10;
      this.$emit("close-modal");
    },
  },
};
</script>

<style>
.v-text-field__details {
  margin-top: 8px;
}
@media (max-width: 1163px) {
  .btn-search-order {
    font-size: 10px;
  }
}
</style>
